import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import './RadialGauge.css';
import { resolveValueColorFromStops } from './liquidShared';

const extractNumericValueFromDatastreams = (datastreams, pin) => {
  if (!datastreams || !pin) return undefined;

  if (typeof datastreams === 'object' && datastreams !== null) {
    if (Object.prototype.hasOwnProperty.call(datastreams, pin)) {
      const n = Number(datastreams[pin]);
      return Number.isFinite(n) ? n : undefined;
    }

    if (datastreams.pins && Object.prototype.hasOwnProperty.call(datastreams.pins, pin)) {
      const n = Number(datastreams.pins[pin]);
      return Number.isFinite(n) ? n : undefined;
    }

    if (datastreams.datastreams && Object.prototype.hasOwnProperty.call(datastreams.datastreams, pin)) {
      const n = Number(datastreams.datastreams[pin]);
      return Number.isFinite(n) ? n : undefined;
    }
  }

  return undefined;
};

const toFiniteNumberOrUndefined = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const resolveControlColor = (value) => {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
};

const getSafeSvgId = (prefix, id) =>
  `${prefix}-${String(id || 'widget').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const RadialGauge = ({
  title,
  transparentBackground,
  min,
  max,
  value,
  unit,
  showUnit = true,
  color,
  unfilledColor,
  startColor,
  stopColor,
  dims,
  draggable,
  pin,
  id,
  decimals,
  colors,
  colorGrading = false,
  changeColorBasedOnValue = false,
  datastreams
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 300, height: 300 });
  const [cursorState, setCursorState] = useState('idle');

  const fontSizeMultiplier = typeof window !== 'undefined'
    ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier') || 1)
    : 1;

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const calculateRadius = useCallback(() => {
    const paddingOffset = 40 * fontSizeMultiplier;
    const availableHeight = Math.max(containerSize.height - paddingOffset, 50);
    const availableWidth = Math.max(containerSize.width, 50);
    return Math.min(availableHeight, availableWidth) / 2;
  }, [containerSize, fontSizeMultiplier]);

  const radius = calculateRadius();

  const resolvedValue = useMemo(() => {
    if (pin) {
      const fromStream = extractNumericValueFromDatastreams(datastreams, pin);
      if (fromStream !== undefined) return fromStream;
    }

    const direct = toFiniteNumberOrUndefined(value);
    return direct;
  }, [value, datastreams, pin]);

  const datastreamMeta = useMemo(() => {
    const list = Array.isArray(datastreams) ? datastreams : [];
    if (!pin) return null;
    return list.find(ds => String(ds?.pin || '') === String(pin)) || null;
  }, [datastreams, pin]);

  const datastreamColor = useMemo(
    () => resolveControlColor(datastreamMeta?.color),
    [datastreamMeta]
  );

  const resolvedUnit = useMemo(() => {
    const dsUnit = datastreamMeta?.unit;
    if (dsUnit !== undefined && dsUnit !== null && String(dsUnit).trim() !== '') return dsUnit;
    return unit;
  }, [datastreamMeta, unit]);

  const minNum = useMemo(() => {
    const n = Number(min);
    return Number.isFinite(n) ? n : 0;
  }, [min]);

  const maxNum = useMemo(() => {
    const n = Number(max);
    return Number.isFinite(n) ? n : 100;
  }, [max]);

  const safeValueForMath = useMemo(() => {
    if (resolvedValue === undefined) return minNum;
    return Math.max(minNum, Math.min(maxNum, resolvedValue));
  }, [resolvedValue, minNum, maxNum]);

  const parameters = useMemo(() => {
    const minVal = minNum;
    const maxVal = maxNum;
    const valueConsidered = Math.max(minVal, Math.min(maxVal, safeValueForMath));
    const range = maxVal - minVal;

    const effectiveRadius = 0.9 * radius;
    const circumference = 2 * Math.PI * effectiveRadius;
    const normalizedValue = range > 0 ? (valueConsidered - minVal) / range : 0;

    const strokeWidth = radius * 0.2;
    const offset = circumference * (1 - normalizedValue);

    const percentageFontSize = Math.max(radius * 0.18, 10 * fontSizeMultiplier);

    return {
      radius,
      innerRadius: effectiveRadius,
      dashArray: circumference,
      offset,
      strokeWidth,
      percentageFontSize,
    };
  }, [minNum, maxNum, safeValueForMath, radius, fontSizeMultiplier]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    el.style.setProperty('--rg-font-mult', String(fontSizeMultiplier));
    el.style.setProperty('--rg-gap', `${Math.max(1 * fontSizeMultiplier, radius * 0.005)}px`);
    el.style.setProperty('--rg-title-font-size', `${Math.max(radius * 0.14, 14 * fontSizeMultiplier)}px`);
    el.style.setProperty('--rg-value-font-size', `${parameters.percentageFontSize}px`);
    el.style.setProperty('--rg-unit-font-size', `${Math.max(parameters.percentageFontSize * 0.4, 8 * fontSizeMultiplier)}px`);
  }, [fontSizeMultiplier, radius, parameters.percentageFontSize]);

  const formattedValue = useMemo(() => {
    const v = resolvedValue === undefined ? undefined : safeValueForMath;
    if (v === undefined) return '—';
    if (!isNaN(v) && isFinite(v) && decimals != null) {
      return parseFloat(parseFloat(v).toFixed(decimals)).toString();
    }
    return v;
  }, [resolvedValue, safeValueForMath, decimals]);

  const getColorForValue = useCallback(() => {
    const v = resolvedValue === undefined ? minNum : safeValueForMath;
    const fixedColor = resolveControlColor(color) || datastreamColor || resolveControlColor(stopColor);
    return resolveValueColorFromStops({
      value: v,
      baseColor: fixedColor || "var(--radialgauge-default-stop, #FF928A)",
      stops: colors,
      enabled: changeColorBasedOnValue,
      grading: colorGrading,
    }) || fixedColor || "var(--radialgauge-default-stop, #FF928A)";
  }, [resolvedValue, safeValueForMath, minNum, colors, colorGrading, changeColorBasedOnValue, color, datastreamColor, stopColor]);

  const shouldUseLegacyFixedGradient =
    !changeColorBasedOnValue && colorGrading && !resolveControlColor(color) && !datastreamColor && (startColor || stopColor);
  const fillColor = useMemo(() => getColorForValue(), [getColorForValue]);
  const trackColor = resolveControlColor(unfilledColor);
  const fillGradientId = getSafeSvgId('grad', id);

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'radial-gauge--grabbing' : 'radial-gauge--grab')
    : 'radial-gauge--auto';

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "radial-gauge",
        cursorClass,
        transparentBackground ? "radial-gauge--transparent" : "",
      ].filter(Boolean).join(" ")}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const widgetId = e.target.closest('.widget')?.id;
        if (!widgetId) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `radial-gauge;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <h3 className="radial-gauge__title">
        {title}
      </h3>

      <div className="radial-gauge__body">
        <svg
          className="radial-gauge__svg"
          width={radius * 2}
          height={radius * 2}
          viewBox={`0 0 ${radius * 2} ${radius * 2}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {shouldUseLegacyFixedGradient && (
              <linearGradient id={fillGradientId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="15%" stopColor={startColor || stopColor || "var(--radialgauge-default-stop, #FF928A)"} stopOpacity="1" />
                <stop offset="85%" stopColor={stopColor} stopOpacity="1" />
              </linearGradient>
            )}
          </defs>

          <circle
            cx={parameters.radius}
            cy={parameters.radius}
            strokeWidth={parameters.strokeWidth}
            r={parameters.innerRadius}
            fill="transparent"
            stroke={trackColor || fillColor}
            strokeOpacity={trackColor ? 1 : 0.24}
            strokeDasharray={parameters.dashArray}
            strokeDashoffset="0"
            transform={`rotate(-90 ${parameters.radius} ${parameters.radius})`}
            strokeLinecap="round"
          />

          <circle
            className="radial-gauge__fill"
            cx={parameters.radius}
            cy={parameters.radius}
            strokeWidth={parameters.strokeWidth}
            r={parameters.innerRadius}
            fill="transparent"
            stroke={
              shouldUseLegacyFixedGradient
                ? `url(#${fillGradientId})`
                : fillColor
            }
            strokeDasharray={parameters.dashArray}
            strokeDashoffset={parameters.offset}
            transform={`rotate(-90 ${parameters.radius} ${parameters.radius})`}
            strokeLinecap="round"
          />
        </svg>

        <div className="radial-gauge__center">
          <div className="radial-gauge__value">
            {formattedValue}
            {showUnit && resolvedUnit != null && String(resolvedUnit).trim() !== ''
              ? ` ${resolvedUnit}`
              : ''}
          </div>

          {showUnit && resolvedUnit != null && String(resolvedUnit).trim() !== '' ? (
            <div className="radial-gauge__unitRow">
              <span className="radial-gauge__unit" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RadialGauge);
